import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { COMMENTS } from "@/constants/testIds";
import { t } from "@/lib/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, Plus, PlayCircle, Trash2, RefreshCw, Sparkles } from "lucide-react";

function fmt(iso) {
  return new Date(iso).toLocaleString("ru-RU", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

const statusStyles = {
  planned: "bg-slate-100 text-slate-700",
  posted: "bg-emerald-100 text-emerald-700",
  skipped: "bg-amber-100 text-amber-700",
};

const emptyForm = { target_url: "", video_topic: "", spread_minutes: 60, account_ids: [] };

export default function Comments() {
  const [list, setList] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([api.listCommentBlasts(), api.accounts()]);
      setList(c); setAccounts(a);
    } catch { toast.error("Не удалось загрузить"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleAccount = (id) => {
    setForm((f) => ({
      ...f,
      account_ids: f.account_ids.includes(id) ? f.account_ids.filter((x) => x !== id) : [...f.account_ids, id],
    }));
  };

  const create = async () => {
    if (!form.target_url || !form.video_topic) return toast.error("URL и тема обязательны");
    if (form.account_ids.length === 0) return toast.error("Выбери хотя бы один аккаунт");
    setBusy(true);
    try {
      const r = await api.createCommentBlast({
        target_url: form.target_url,
        video_topic: form.video_topic,
        spread_minutes: Number(form.spread_minutes) || 60,
        account_ids: form.account_ids,
      });
      toast.success(t.comments.queued(r.count));
      setOpen(false); setForm(emptyForm); load();
    } catch { toast.error(t.common.failed); }
    finally { setBusy(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.runCommentBlast();
      toast.success(t.comments.posted(r.posted));
      load();
    } catch { toast.error(t.common.failed); }
    finally { setRunning(false); }
  };

  const del = async (id) => {
    if (!window.confirm(t.common.confirmDelete)) return;
    try { await api.deleteCommentBlast(id); load(); }
    catch { toast.error(t.common.failed); }
  };

  const accountFor = (id) => accounts.find((a) => a.id === id);

  return (
    <div data-testid={COMMENTS.root}>
      <PageHeader
        title={t.comments.title}
        description={t.comments.description}
        actions={
          <div className="flex gap-2">
            <button
              data-testid={COMMENTS.runBtn}
              onClick={runNow}
              disabled={running}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium disabled:opacity-50 transition-colors"
            >
              {running ? <RefreshCw className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
              {running ? t.comments.posting : t.comments.postDue}
            </button>
            <button
              data-testid={COMMENTS.createBtn}
              onClick={() => { setForm(emptyForm); setOpen(true); }}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
            >
              <Plus className="h-4 w-4" /> {t.comments.new}
            </button>
          </div>
        }
      />

      {loading ? (
        <div className="text-slate-400 text-sm">{t.common.loading}</div>
      ) : list.length === 0 ? (
        <div className="card-soft p-10 text-center">
          <MessageCircle className="h-10 w-10 text-emerald-300 mx-auto" />
          <div className="font-display text-lg mt-3 text-slate-900">{t.comments.empty}</div>
          <div className="text-sm text-slate-500 mt-1">{t.comments.emptyHint}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((c) => {
            const a = accountFor(c.account_id);
            return (
              <div key={c.id} className="card-soft p-4 flex items-start gap-4">
                <div className="h-10 w-10 shrink-0 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <MessageCircle className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-medium text-slate-900">{a?.username || "?"}</div>
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ${statusStyles[c.status]}`}>
                      {t.comments.status[c.status]}
                    </span>
                    <span className="text-xs text-slate-500 font-mono truncate max-w-md">{c.target_url}</span>
                  </div>
                  <div className="text-sm text-slate-700 mt-1 italic">&laquo;{c.comment}&raquo;</div>
                  <div className="text-[11px] text-slate-400 mt-1 font-mono">запланировано {fmt(c.planned_at)}</div>
                </div>
                <button
                  onClick={() => del(c.id)}
                  className="text-slate-300 hover:text-rose-500 transition-colors shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-emerald-500" /> {t.comments.new}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.comments.fields.url}</Label>
              <Input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="https://tiktok.com/@you/video/..." className="mt-1 font-mono text-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.comments.fields.topic}</Label>
              <Textarea value={form.video_topic} onChange={(e) => setForm({ ...form, video_topic: e.target.value })} placeholder="30-с ролик про паттерн двойного дна на 15м графике BTC" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.comments.fields.spread}</Label>
              <Input type="number" value={form.spread_minutes} onChange={(e) => setForm({ ...form, spread_minutes: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.comments.fields.accounts}</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {accounts.map((a) => {
                  const on = form.account_ids.includes(a.id);
                  return (
                    <button
                      key={a.id}
                      onClick={() => toggleAccount(a.id)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        on ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-emerald-300"
                      }`}
                    >
                      {a.username}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid={COMMENTS.saveBtn}
              onClick={create}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-medium transition-colors"
            >
              {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {busy ? t.comments.creating : t.comments.create}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
